import { InfoPage } from '@/components/info-page';
import { getCoachSessionPayment, getUser } from '@/lib/api-calls';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { InfoCard } from '@/components/info-card';
import { InfoKeyValue } from '@/components/info-key-value';
import { airColumnHelper } from '@/lib/tables';
import {
  CoachPaymentSessionDetails,
  coachPaymentSessionDetailsSchema,
  formatCoachSessionRemarkV2Label,
  PayCycleDTO,
  VideoConferenceTypeLabels,
} from '@airreading/common/data';
import { EntityTable } from '@/components/table/entity-table';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { formatInstant, formatPlainDate, FrequentTimeZones } from '@airreading/common';
import { formatCurrency } from '@/lib/common-utils';
import { SessionCompletionStatusCell } from '@/routes/_layout/sessions/-components/session-completion-status-cell';
import { SessionFeedbackStatus } from '@/components/session/session-feedback-status';
import { useErrorToast } from '@/lib/error-handling';
import { paginationRequestSchema } from '@/lib/pagination';
import { usePaginatedFilters } from '@/lib/filtering';
import { Badge } from '@/components/ui/badge';

export const Route = createFileRoute('/_layout/teachers/$teacherId/payments/teaching/$payCycleId/')({
  component: TeachSessionPaymentsDetails,
  validateSearch: paginationRequestSchema,
});

const columnHelper = airColumnHelper<CoachPaymentSessionDetails>(coachPaymentSessionDetailsSchema.keyof());

const coachPaymentReportTableColumns = [
  columnHelper.accessor((row) => row, {
    id: 'classTime',
    header: 'Class times (CT)',

    cell: ({ getValue }) => {
      const session = getValue();
      if (!session) {
        return '';
      }

      const startDateTime = formatInstant(session.startInstant, FrequentTimeZones.UsCentral);
      const endDateTime = formatInstant(session.endInstant, FrequentTimeZones.UsCentral);

      const [month, day] = startDateTime.formattedDate.split('/');

      return `${month}/${day}, ${startDateTime.formattedTime} - ${endDateTime.formattedTime}`;
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'duration',
    header: 'Session duration',

    cell: ({ getValue }) => {
      const session = getValue();
      if (!session) {
        return '';
      }

      const duration = session.endInstant.since(session.startInstant);

      const minutes = duration.total({ unit: 'minute' });

      return `${minutes} mins (${VideoConferenceTypeLabels[session.videoConferenceType]})`;
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'meetingStatus',
    header: 'Meeting status',
    cell: ({ getValue }) => {
      const session = getValue();
      if (!session) {
        return '';
      }

      const { sessionCompletionStatus, coachSessionRemarksV2 } = session;

      return (
        <SessionCompletionStatusCell
          status={sessionCompletionStatus}
          coachSessionRemarksV2={coachSessionRemarksV2}
          session={session}
        />
      );
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'feedback',
    header: 'Feedback',
    cell: ({ getValue }) => {
      const session = getValue();
      if (!session) {
        return '';
      }

      const { hoursCanceledBeforeStart, sessionFeedbacks, lesson } = session;

      return (
        <SessionFeedbackStatus
          feedbacks={sessionFeedbacks || []}
          hoursCanceledBeforeStart={hoursCanceledBeforeStart}
          lessonShouldSkipFeedback={lesson.shouldSkipFeedback}
        />
      );
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'pay',
    header: 'Pay',

    cell: ({ getValue }) => {
      const session = getValue();

      return formatCurrency((session?.baseAmount ?? 0) / 100);
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'incentive',
    header: 'Incentive',

    cell: ({ getValue }) => {
      const session = getValue();

      return formatCurrency((session?.incentivesAmount ?? 0) / 100);
    },
  }),

  columnHelper.accessor((row) => row, {
    id: 'remarks',
    header: 'Session remark',

    cell: ({ getValue }) => {
      const session = getValue();

      if (!session) {
        return '-';
      }

      const { coachSessionRemarksV2 } = session;

      return (
        <div className="flex flex-col gap-1 items-center">
          {coachSessionRemarksV2?.map((tag) => (
            <Badge key={tag} variant="outline" className="text-center">
              {formatCoachSessionRemarkV2Label(tag, session)}
            </Badge>
          ))}
        </div>
      );
    },
  }),
];

function TeachSessionPaymentsDetails() {
  const { teacherId, payCycleId } = Route.useParams();

  const { data: user } = useQuery({
    queryKey: ['user', teacherId],
    queryFn: () => getUser(teacherId),
  });

  const { pageIndex, pageSize, onPaginationChange } = usePaginatedFilters(Route.id, ['pageIndex', 'pageSize']);

  const { data, error, isPending, refetch } = useQuery(
    getCoachSessionPayment({
      coachId: teacherId,
      filters: {
        pageIndex,
        pageSize,
        filters: {
          payCycleId,
        },
      },
    }),
  );

  useErrorToast(error, 'Error loading payments. Please try again');

  const table = useReactTable({
    data: data?.payments || [],
    columns: coachPaymentReportTableColumns,
    getRowId: (row) => row.id,
    rowCount: data?.total || 0,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    state: {
      pagination: {
        pageIndex: pageIndex,
        pageSize: pageSize,
      },
    },
  });

  return (
    <InfoPage
      headerText={user?.fullNameOrEmail || 'Payments'}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Teachers',
            link: {
              to: '/teachers',
            },
          },
          {
            label: 'Teacher details',
            link: {
              to: '/teachers/$teacherId/payments',
            },
          },
        ],
        currentPage: 'Payments',
      }}
    >
      <InfoCard headerText={'Teaching'}>
        <InfoKeyValue label={'Gross / Net Earnings'} value={formatCurrency((data?.totalAmountInCents ?? 0) / 100)} />
        <InfoKeyValue
          label={'Period'}
          value={`
            ${formatPlainDate(PayCycleDTO.fromPayPeriodId(payCycleId).startPlainDate)} - ${formatPlainDate(PayCycleDTO.fromPayPeriodId(payCycleId).endPlainDate)}
            `}
        />
        <InfoKeyValue
          label={'Pay Initiation Date'}
          value={formatPlainDate(PayCycleDTO.fromPayPeriodId(payCycleId).paymentPlainDate)}
        />
        <InfoKeyValue label={'Delivery (1-3 business days)'} value={data?.depositPostedDate} />
      </InfoCard>
      <EntityTable
        table={table}
        showPagination
        isLoading={isPending}
        hasError={!!error}
        emptyState="No payments found"
        errorState={<TableErrorState refetchAction={refetch} />}
      />
    </InfoPage>
  );
}
